import { Component, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '../authentication/authentication.service';
import { RolesEnum } from '../models/enums';
import { IBreadCrumb } from './bread-crump/model/ibread-crumb';
import { AttachmentService } from '../services/attachment-service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'base-component'
})
export class BaseComponent {
  NullValue?: any = null;
  currentDate: Date = new Date();
  public currentCulture!: string;
  public isArabic: boolean = true;
  public pageTitle: string = '';
  breadcrumbItems?: IBreadCrumb[] = [];
  public apiUrl = environment.APIURL;
  activeLink!: number;
  protected localizer: TranslateService;
  protected activatedRoute: ActivatedRoute;
  protected _router: Router;
  protected authService: AuthenticationService;
  protected attachementService?: AttachmentService;
  protected routerBase?: Router;
  protected routeBase?: ActivatedRoute;
  protected translate: TranslateService;
  protected toastrService: ToastrService;
  constructor() {
    this.initGlobalVariables();
    this.localizer = inject(TranslateService);
    this.activatedRoute = inject(ActivatedRoute);
    this._router = inject(Router);
    this.authService = inject(AuthenticationService);
    this.attachementService = inject(AttachmentService);
    this.routerBase = inject(Router);
    this.routeBase = inject(ActivatedRoute);
    this.translate = inject(TranslateService);
    this.toastrService = inject(ToastrService);
  }

  initGlobalVariables() {
    let lang = localStorage.getItem("lang");
    this.currentCulture = lang == 'ar' ? 'ar-EG' : 'en-US';
    this.setCurrentCulture();
  }

  setCurrentCulture() {
    let lang = localStorage.getItem("lang");
    this.isArabic = lang == 'ar' ? true : false;
  }

  public get Roles(): typeof RolesEnum {
    return RolesEnum;
  }

  public RoleName(Role: RolesEnum): string {
    return RolesEnum[Role];
  }

  isAuthenticated(): boolean {
    return this.authService?.isUserAuthenticated() ?? false;
  }

  public toLowerParams(params: Params): Params {
    const lowerParams: Params = {};
    for (const key in params) {
      lowerParams[key.toLowerCase()] = params[key];
    }
    return lowerParams;
  }

  loadJs() {
    const element = document.getElementById('customScriptFile');
    if (element) element.remove();
    let customScriptFile = document.createElement('script');
    customScriptFile.type = 'text/javascript';
    customScriptFile.src = '/assets/js/script.js';
    customScriptFile.id = 'customScriptFile';
    document.body.appendChild(customScriptFile);
  }

  /**
   * Function used to download file in browser by making anchor tag and click it to download then remove tag again
   * Create download link and click it to download file directly
   * @param data File content to be downloaded (in stringbase64 or byte[])
   * @param data_ApplicationMemType File mem type Ex: application/pdf
   * @param downloadedFileName_TranslationKey TranslationKey string to be set as downloaded file name
   */
  download(
    data: any,
    data_ApplicationMemType: string,
    downloadedFileName_TranslationKey: string
  ) {
    const src = `data:${data_ApplicationMemType};base64;,${data}`;
    const link = document.createElement('a');
    link.href = src;
    link.download = downloadedFileName_TranslationKey;
    link.click();
    link.remove();
  }

  async ViewBlank(data: string, data_ApplicationMemType: string) {
    const base64File = data;
    const base64Response = await fetch(
      `data:` + data_ApplicationMemType + `;base64,${base64File}`
    );
    const blob = base64Response.blob();
    const url = window.URL.createObjectURL(await blob);
    window.open(url, '_blank');
  }

  confirm(
    title?: string,
    confirmButtonText?: string,
    message?: string,
    submitIcon?: string
  ) {
    let _html = '<h3 class="modal-title">' + title + '</h3>';
    if (message) _html += '<div class="mt-3">' + message + '</div>';

    return Swal.fire({
      html: _html,
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: this.isArabic ? 'إغلاق' : 'Cancel',
      confirmButtonText: confirmButtonText
        ? (submitIcon ? submitIcon : '') + ' ' + confirmButtonText
        : this.isArabic
          ? 'نعم'
          : 'Yes',
      allowOutsideClick: false,
      customClass: {
        cancelButton: 'btn btn-outline-primary',
        confirmButton: 'btn btn-primary',
      },
    });
  }

  deleteConfirm(title: string, message?: string) {
    let _html = '<h3 class="modal-title">' + title + '</h3>';
    if (message) _html += '<div class="mt-3">' + message + '</div>';

    return Swal.fire({
      html: _html,
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: this.isArabic ? 'لا' : 'No',
      confirmButtonText: this.isArabic ? 'نعم' : 'Yes',
      allowOutsideClick: false,
      customClass: {
        icon: 'swal2-delete',
        cancelButton: 'btn btn-outline-primary',
        confirmButton: 'btn btn-danger',
      },
    });
  }

  successSwal(title: string, message?: string) {
    let _html = '<h3 class="modal-title">' + title + '</h3>';
    if (message) _html += '<div class="mt-3">' + message + '</div>';

    Swal.fire({
      // iconHtml: `
      // <span class="icon-success">
      //   <svg width="54" height="39" viewBox="0 0 54 39" fill="none" xmlns="http://www.w3.org/2000/svg">
      //     <path fill-rule="evenodd" clip-rule="evenodd" d="M52.5578 1.77949C54.0361 3.26351 54.0361 5.66952 52.5578 7.15354L22.272 37.5536C20.7937 39.0375 18.3966 39.0375 16.9182 37.5536L1.7753 22.3535C0.296904 20.8696 0.296904 18.4635 1.7753 16.9796C3.25374 15.4957 5.65071 15.4957 7.12915 16.9796L19.5951 29.4926L47.204 1.77949C48.6823 0.295509 51.0794 0.295509 52.5578 1.77949Z" fill="#23A566"/>
      //   </svg>
      // </span>
      // `,
      icon: 'success',
      html: _html,
      showCancelButton: false,
      confirmButtonText: this.isArabic ? 'موافق' : 'OK',
      customClass: {
        confirmButton: 'submit btn btn-primary',
      },
    });
  }

  errorSwal(title: string, message?: string) {
    let _html = '<h3 class="modal-title">' + title + '</h3>';
    if (message) _html += '<div class="mt-3">' + message + '</div>';

    return Swal.fire({
      html: _html,
      icon: 'error',
      showCancelButton: false,
      confirmButtonText: this.isArabic ? 'موافق' : 'OK',
      customClass: {
        icon: 'swal2-error',
        confirmButton: 'btn btn-primary',
      },
    });
  }

  error(message: string) {
    Swal.fire({
      icon: 'error',
      html: `<h3 class="modal-title">` + message + `</h3>`,
      showCancelButton: false,
      confirmButtonText: this.isArabic ? 'موافق' : 'OK',
      customClass: {
        confirmButton: 'btn btn-primary',
      },
    });
  }

  navigate(urlPath: string, skipLocationChange: boolean = false) {
    this.routerBase?.navigate([urlPath], {
      relativeTo: this.routeBase,
      skipLocationChange: skipLocationChange,
    });
  }

  goBack() {
    window.history.back();
  }

  pagedItems: any[] | undefined;

  getUserRole() {
    var roles = this.authService.getAuthenticatedUser().role;
    var role = Array.isArray(roles) ? roles[0] : roles;
    return role;
  }

  getUserDisplayRole() {
    if (this.isArabic) {
      var roles = this.authService.getAuthenticatedUser().roleAr;
      var role = Array.isArray(roles) ? roles[0] : roles;
      return role;
    }
    else {
      var roles = this.authService.getAuthenticatedUser().roleEn;
      var role = Array.isArray(roles) ? roles[0] : roles;
      return role;
    }

  }

  getUserName_() {

    return  this.authService.getAuthenticatedUser().userName;
  }
  getUserName() {

    return this.isArabic ? this.authService.getAuthenticatedUser().fullnameAr : this.authService.getAuthenticatedUser().fullnameEn;
  }

  getImagePath(imageData: string, contentType: string): string {
    if (!imageData) return '';
    
    // Check if the data is already a base64 string
    if (imageData.startsWith('data:image')) {
      return imageData;
    }
    
    // Convert the byte array string to base64
    return `data:${contentType};base64,${imageData}`;
  }

  get getUserId() {
    return this.authService.getAuthenticatedUser().uid;
  }

  isImage(file: File): boolean {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    return allowedTypes.includes(file.type);
  }
  isPDF(file: File): boolean {
    const allowedTypes = ['application/pdf'];
    return allowedTypes.includes(file.type);
  }
  isWord(file: File): boolean {
    const allowedTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    return allowedTypes.includes(file.type);
  }
  isExcel(file: File): boolean {
    const allowedTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    ];
    return allowedTypes.includes(file.type);
  }
  checkAcceptedFileTypes(file: File,): boolean {
    let extensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    let fileExtension = file.name.split('.').pop() || '';
    return extensions.indexOf(fileExtension) >= 0;
  }

  IsAuthorize(roles: Array<string>): boolean {

    return this.authService?.hasAnyRoleOf(roles) ?? false;
  }

  calculateDaysBetween(startDate: Date | string, endDate: Date | string): number {
    const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
    const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

    const timeDifference = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(timeDifference / (1000 * 3600 * 24));
  }
  calculateDuration(totalDays: number): string {
    if (this.isArabic)
      return this.convertDaysToArabicString(totalDays)
    else
      return this.convertDaysToEnglishString(totalDays)
  }
  convertDaysToArabicString(totalDays: number): string {
    if (totalDays === 0) {
      return 'يوم واحد';
    }

    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const weeks = Math.floor(((totalDays % 365) % 30) / 7);
    const days = ((totalDays % 365) % 30) % 7;

    const parts: string[] = [];

    // إضافة السنوات
    if (years > 0) {
      if (years === 1) {
        parts.push('سنة واحدة');
      } else if (years === 2) {
        parts.push('سنتين');
      } else if (years >= 3 && years <= 10) {
        parts.push(`${years} سنوات`);
      } else {
        parts.push(`${years} سنة`);
      }
    }

    // إضافة الأشهر
    if (months > 0) {
      if (months === 1) {
        parts.push('شهر واحد');
      } else if (months === 2) {
        parts.push('شهرين');
      } else if (months >= 3 && months <= 10) {
        parts.push(`${months} أشهر`);
      } else {
        parts.push(`${months} شهراً`);
      }
    }

    // إضافة الأسابيع
    if (weeks > 0) {
      if (weeks === 1) {
        parts.push('أسبوع واحد');
      } else if (weeks === 2) {
        parts.push('أسبوعين');
      } else if (weeks >= 3 && weeks <= 10) {
        parts.push(`${weeks} أسابيع`);
      } else {
        parts.push(`${weeks} أسبوعاً`);
      }
    }

    // إضافة الأيام
    if (days > 0) {
      if (days === 1) {
        parts.push('يوم واحد');
      } else if (days === 2) {
        parts.push('يومين');
      } else if (days >= 3 && days <= 10) {
        parts.push(`${days} أيام`);
      } else {
        parts.push(`${days} يوماً`);
      }
    }

    // ربط الأجزاء بـ "و"
    if (parts.length === 0) {
      return 'لا توجد أيام';
    } else if (parts.length === 1) {
      return parts[0];
    } else {
      const lastPart = parts.pop();
      return parts.join(' و ') + ' و ' + lastPart;
    }
  }
  convertDaysToEnglishString(totalDays: number): string {
    if (totalDays === 0) {
      return 'No days';
    }

    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const weeks = Math.floor(((totalDays % 365) % 30) / 7);
    const days = ((totalDays % 365) % 30) % 7;

    const parts: string[] = [];

    // Add years
    if (years > 0) {
      parts.push(years === 1 ? '1 year' : `${years} years`);
    }

    // Add months
    if (months > 0) {
      parts.push(months === 1 ? '1 month' : `${months} months`);
    }

    // Add weeks
    if (weeks > 0) {
      parts.push(weeks === 1 ? '1 week' : `${weeks} weeks`);
    }

    // Add days
    if (days > 0) {
      parts.push(days === 1 ? '1 day' : `${days} days`);
    }

    // Join parts with commas and "and"
    if (parts.length === 0) {
      return 'No time';
    } else if (parts.length === 1) {
      return parts[0];
    } else if (parts.length === 2) {
      return parts.join(' and ');
    } else {
      const lastPart = parts.pop();
      return parts.join(', ') + ', and ' + lastPart;
    }
  }
  convertToDate(date : any) {
    return new Date(date);
  }

  /**
   * Checks if a form field has the required validator
   * @param fieldName The name of the form control
   * @param form The form group (defaults to this.createForm if available)
   */
  isFieldRequired(fieldName: string, form?: FormGroup): boolean {
    // Use provided form, or try to find createForm (skip 'form' getter as it may return controls object)
    const fg = form || (this as any).createForm;
    if (!fg) return false;

    // If fg is not a FormGroup (no .get method), try to get the control directly
    const control = typeof fg.get === 'function' ? fg.get(fieldName) : (fg as any)[fieldName];
    if (!control) return false;

    // Check if the control has the RequiredValidator
    return control.hasValidator(Validators.required);
  }
}
